import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  HiOutlineEnvelope,
  HiOutlinePencilSquare,
  HiOutlinePlus,
  HiOutlineShieldCheck,
  HiOutlineUserGroup,
  HiOutlineNoSymbol,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";
import Modal from "../../../components/ui/modal/Modal";
import ConfirmModal from "../../../components/ui/confirmModal/ConfirmModal";

import UserForm from "../../../components/forms/user/UserForm";

import {
  disableUser,
  getUsers,
  resendSetupInvite,
  setUserFilters,
} from "../../../redux/features/users/userSlice";

import {
  UserRoleBadge,
  UserStatusBadge,
} from "../../../components/users/UserBadges";

import usePermissions from "../../../hooks/usePermissions";

import "./UsersPage.css";

export default function UsersPage() {
  const dispatch = useDispatch();

  const { can } = usePermissions();

  const { users, pagination, filters, isLoading, isInviting, error } =
    useSelector((state) => state.users);

  const [openCreateModal, setOpenCreateModal] = useState(false);

  const [openEditModal, setOpenEditModal] = useState(false);

  const [selectedEditUser, setSelectedEditUser] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Bevestigen",
    variant: "danger",
    onConfirm: null,
  });

  useEffect(() => {
    dispatch(
      getUsers({
        page: pagination.page || 1,
        limit: pagination.limit || 20,
        ...filters,
      }),
    );
  }, [dispatch, filters, pagination.page, pagination.limit]);

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: "",
      message: "",
      confirmText: "Bevestigen",
      variant: "danger",
      onConfirm: null,
    });
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;

    dispatch(
      setUserFilters({
        [name]: value,
      }),
    );
  };

  const openEdit = (user) => {
    setSelectedEditUser(user);
    setOpenEditModal(true);
  };

  const openDisableConfirm = (user) => {
    setConfirmModal({
      isOpen: true,
      title: "Gebruiker uitschakelen?",
      message: `${user.first_name} ${user.last_name} kan daarna niet meer inloggen.`,
      confirmText: "Uitschakelen",
      variant: "danger",
      onConfirm: async () => {
        await dispatch(disableUser(user._id)).unwrap();
        closeConfirmModal();
      },
    });
  };

  const handleResendInvite = async (user) => {
    try {
      await dispatch(resendSetupInvite(user._id)).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  if (isLoading && users.length === 0) {
    return <PageLoader title="Gebruikers laden..." />;
  }

  return (
    <div className="users-page">
      <PageHeader
        eyebrow="POWER"
        title="Gebruikersbeheer"
        description="Beheer admins, interne coaches en externe coaches."
        actions={
          can("user.create") && (
            <button
              className="users-page__create-button"
              onClick={() => setOpenCreateModal(true)}
            >
              <HiOutlinePlus />
              <span>Nieuwe gebruiker</span>
            </button>
          )
        }
      />

      {error && <div className="users-page__error">{error}</div>}

      <PageCard className="users-page__filters-card">
        <div className="users-page__filters">
          <input
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Zoeken op naam of e-mail..."
          />

          <select
            name="role"
            value={filters.role}
            onChange={handleFilterChange}
          >
            <option value="">Alle rollen</option>
            <option value="POWER_ADMIN">Admin</option>
            <option value="POWER_INTERNAL_COACH">Interne coach</option>
            <option value="POWER_EXTERNAL_COACH">Externe coach</option>
          </select>

          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="">Alle statussen</option>
            <option value="INVITED">Uitgenodigd</option>
            <option value="ACTIVE">Actief</option>
            <option value="BLOCKED">Geblokkeerd</option>
            <option value="DISABLED">Uitgeschakeld</option>
          </select>

          <select
            name="auth_provider"
            value={filters.auth_provider}
            onChange={handleFilterChange}
          >
            <option value="">Alle providers</option>
            <option value="core">Core</option>
            <option value="local">Local</option>
          </select>
        </div>
      </PageCard>

      {users.length === 0 ? (
        <PageEmpty
          icon={<HiOutlineUserGroup />}
          title="Geen gebruikers gevonden"
          description="Er zijn geen gebruikers die overeenkomen met deze filters."
        />
      ) : (
        <PageCard className="users-page__table-card">
          <div className="users-page__table-wrapper">
            <table className="users-page__table">
              <thead>
                <tr>
                  <th>Naam</th>
                  <th>E-mail</th>
                  <th>Rol</th>
                  <th>Status</th>
                  <th>Provider</th>
                  <th>MFA</th>
                  <th>Laatste login</th>
                  <th>Acties</th>
                </tr>
              </thead>

              <tbody>
                {users.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <strong>
                        {user.first_name} {user.last_name}
                      </strong>
                    </td>

                    <td>{user.email}</td>

                    <td>
                      <UserRoleBadge role={user.role} />
                    </td>

                    <td>
                      <UserStatusBadge status={user.status} />
                    </td>

                    <td>{user.auth_provider}</td>

                    <td>{user.mfa_enabled ? "Actief" : "Niet actief"}</td>

                    <td>
                      {user.last_login_at
                        ? new Date(user.last_login_at).toLocaleDateString(
                            "nl-BE",
                          )
                        : "-"}
                    </td>

                    <td>
                      <div className="users-page__actions">
                        {can("user.update") && (
                          <button
                            onClick={() => openEdit(user)}
                            title="Wijzigen"
                          >
                            <HiOutlinePencilSquare />
                          </button>
                        )}

                        {can("user.permissions") && (
                          <button title="Rechten">
                            <HiOutlineShieldCheck />
                          </button>
                        )}

                        {can("user.invite") &&
                          user.role === "POWER_EXTERNAL_COACH" &&
                          user.status === "INVITED" && (
                            <button
                              disabled={isInviting}
                              onClick={() => handleResendInvite(user)}
                              title="Uitnodiging opnieuw verzenden"
                            >
                              <HiOutlineEnvelope />
                            </button>
                          )}

                        {can("user.disable") && user.status !== "DISABLED" && (
                          <button
                            className="users-page__danger"
                            onClick={() => openDisableConfirm(user)}
                            title="Uitschakelen"
                          >
                            <HiOutlineNoSymbol />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PageCard>
      )}

      <Modal
        isOpen={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        title="Nieuwe gebruiker"
        description="Maak een nieuwe POWER gebruiker aan."
      >
        <UserForm
          mode="create"
          onSuccess={() => {
            setOpenCreateModal(false);
            dispatch(getUsers({ ...filters }));
          }}
          onCancel={() => setOpenCreateModal(false)}
        />
      </Modal>

      <Modal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        title="Gebruiker wijzigen"
        description="Wijzig de basisgegevens van deze gebruiker."
      >
        <UserForm
          mode="edit"
          initialData={selectedEditUser}
          onSuccess={() => setOpenEditModal(false)}
          onCancel={() => setOpenEditModal(false)}
        />
      </Modal>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        variant={confirmModal.variant}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
      />
    </div>
  );
}
